// Client-side recognition of AHP slash commands handled outside chat turns.
// Grammar mirrors copilot-agent-runtime's allow_all.rs and share.rs.

import {APPROVAL_MODES, type SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'

/** A recognized tool-permission command. `show`/`invalid` carry no relay action. */
export type PermissionCommand =
  | {kind: 'set'; mode: SessionApprovalMode}
  | {kind: 'show'}
  | {kind: 'invalid'; arg: string}

export const PERMISSIONS_COMMAND_NAME = '/permissions'
export const ALLOW_ALL_COMMAND_NAME = '/allow-all'

type ShareCommand = {kind: 'share'} | {kind: 'unshare'} | {kind: 'invalid'}

const ALLOW_ALL_NAMES = new Set([ALLOW_ALL_COMMAND_NAME, '/yolo'])

// `/allow-all` predates the three-way modes, so its boolean grammar maps onto them.
// A Map rather than an object literal: the argument is user text, and a plain
// object would resolve inherited keys like `constructor` to a truthy non-mode.
const ALLOW_ALL_ARG_MODES = new Map<string, SessionApprovalMode>([
  ['', 'allow-all'],
  ['on', 'allow-all'],
  ['off', 'manual'],
])

function splitCommand(text: string): {name: string; arg: string} {
  const trimmed = text.trim()
  const firstSpace = trimmed.search(/\s/)
  return {
    name: (firstSpace === -1 ? trimmed : trimmed.slice(0, firstSpace)).toLowerCase(),
    arg: (firstSpace === -1 ? '' : trimmed.slice(firstSpace + 1)).trim().toLowerCase(),
  }
}

/**
 * Parse `text` as a tool-permission command. Returns `null` when it is not one
 * (caller dispatches a normal turn).
 *
 * `/permissions` takes a mode name directly. `/allow-all` (and its `/yolo`
 * alias) keeps its original on/off grammar, resolved to the same modes so both
 * spellings reach one handler. Path and URL arguments are not supported over the
 * relay, so they resolve to `invalid`.
 */
export function parsePermissionCommand(text: string): PermissionCommand | null {
  const {name, arg} = splitCommand(text)

  if (name === PERMISSIONS_COMMAND_NAME) {
    // A bare `/permissions` names no mode, so report the current one rather than
    // dispatching the text to the model as a chat turn.
    if (arg === '' || arg === 'show') return {kind: 'show'}
    const mode = APPROVAL_MODES.find(candidate => candidate === arg)
    return mode ? {kind: 'set', mode} : {kind: 'invalid', arg}
  }

  if (!ALLOW_ALL_NAMES.has(name)) return null

  if (arg === 'show') return {kind: 'show'}
  const mode = ALLOW_ALL_ARG_MODES.get(arg)
  return mode ? {kind: 'set', mode} : {kind: 'invalid', arg}
}

// Runtime commands that inspect the session's git checkout. Without one they
// still run, but only to send an agent looking for a repository that is not there.
const REPOSITORY_COMMAND_NAMES = new Set(['/review', '/security-review', '/init', '/blame'])

/**
 * The name of the repository-dependent command `text` runs, or `null` when it
 * runs none. Forms the runtime answers without a checkout are excluded: a bare
 * `/blame` replies with usage help, and `/init suppress` only records a setting.
 */
export function parseRepositoryCommand(text: string): string | null {
  const {name, arg} = splitCommand(text)
  if (!REPOSITORY_COMMAND_NAMES.has(name)) return null
  if (name === '/blame' && arg === '') return null
  if (name === '/init' && arg === 'suppress') return null
  return name
}

/**
 * Whether `text` runs `/remote`, with any argument. It makes a CLI session
 * reachable from GitHub; a web session already is, so the slash command menu
 * never offers it.
 */
export function isRemoteCommand(text: string): boolean {
  return splitCommand(text).name === '/remote'
}

export function parseShareCommand(text: string): ShareCommand | null {
  const {name, arg} = splitCommand(text)
  if (name !== '/share') return null

  const args = arg ? arg.split(/\s+/) : []
  if (args[0] === 'link') args.shift()
  if (args.length === 0) return {kind: 'share'}
  if (args.length === 1 && args[0] === 'off') return {kind: 'unshare'}
  return {kind: 'invalid'}
}
